import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useCommand } from "../../store";
import { useI18n } from "../../i18n";
import { useColors } from "../../ui";
import { uuidv7 } from "../../../../shared/src/ids";
import { storeBlob } from "../../blobs";
import { BlobImage, BlobVideo, BlobFile } from "../../components/BlobDisplay";
import { Lightbox } from "../../components/Lightbox";

const THUMB = 80;

export function AttachmentsSection({
  caseId,
  attachments,
  readOnly,
}: {
  caseId: string;
  attachments: Array<{
    id: string;
    sha256: string | null;
    mimeType: string | null;
    capturedAt: number | null;
  }>;
  readOnly?: boolean;
}) {
  const dispatch = useCommand();
  const { t } = useI18n();
  const colors = useColors();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Close popover on click outside
  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [popoverOpen]);

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    setPopoverOpen(false);
    for (const file of Array.from(files)) {
      // Store blob first so content is available from OPFS before the attachment appears
      const buffer = await file.arrayBuffer();
      const sha256 = await storeBlob(new Uint8Array(buffer), file.type);
      // Create + setData back-to-back: attachment appears with sha256 already set, no "Uploading..." flash
      const attId = uuidv7();
      await dispatch("attachment.create", {
        uuid: attId,
        caseUuid: caseId,
        mimeType: file.type,
        capturedAt: Date.now(),
      } as any);
      await dispatch("attachment.setData", { uuid: attId, sha256 } as any);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await handleFiles(e.target.files);
    e.target.value = "";
  };

  const viewable = attachments.filter(
    (a): a is typeof a & { sha256: string; mimeType: string } =>
      !!a.sha256 && !!a.mimeType,
  );

  const openAttachment = (attId: string) => {
    const idx = viewable.findIndex((a) => a.id === attId);
    if (idx >= 0) setLightboxIndex(idx);
  };

  if (attachments.length === 0 && readOnly) return null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        padding: "4px 0",
        alignItems: "flex-start",
      }}
    >
      {/* Existing attachments */}
      {attachments.map((att) => {
        const mime = att.mimeType || "";
        const isImage = mime.startsWith("image/");
        const isVideo = mime.startsWith("video/");
        const canOpen = !!att.sha256;

        return (
          <div
            key={att.id}
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              cursor: canOpen ? "pointer" : "default",
            }}
            onClick={() => canOpen && openAttachment(att.id)}
          >
            {isImage && att.sha256 && (
              <BlobImage
                sha256={att.sha256}
                mimeType={mime}
                style={{
                  width: `${THUMB}px`,
                  height: `${THUMB}px`,
                  objectFit: "cover",
                  border: `1px solid ${colors.border}`,
                  borderRadius: "4px",
                }}
              />
            )}
            {isVideo && att.sha256 && (
              <BlobVideo
                sha256={att.sha256}
                mimeType={mime}
                style={{
                  width: `${THUMB}px`,
                  height: `${THUMB}px`,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "4px",
                }}
              />
            )}
            {!isImage && !isVideo && att.sha256 && (
              <BlobFile
                sha256={att.sha256}
                mimeType={mime || "application/octet-stream"}
                style={{
                  width: `${THUMB}px`,
                  height: `${THUMB}px`,
                  borderRadius: "4px",
                }}
              />
            )}
            {!att.sha256 && (
              <div
                style={{
                  width: `${THUMB}px`,
                  height: `${THUMB}px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: colors.bgAlt,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "4px",
                  color: colors.textMuted,
                  fontSize: "0.6rem",
                }}
              >
                Uploading...
              </div>
            )}
            {!readOnly && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch("attachment.delete", { uuid: att.id } as any);
                }}
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  background: colors.danger,
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.7rem",
                  lineHeight: 1,
                }}
              >
                {"\u00D7"}
              </button>
            )}
          </div>
        );
      })}

      {/* + Attach button */}
      {!readOnly && (
        <button
          onClick={() => setPopoverOpen(true)}
          style={{
            width: `${THUMB}px`,
            height: `${THUMB}px`,
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            padding: "4px",
            fontSize: "0.75rem",
            lineHeight: 1,
            cursor: "pointer",
            border: `1px dashed ${colors.border}`,
            borderRadius: "4px",
            background: colors.bgAlt,
            color: colors.text,
          }}
        >
          <span style={{ fontSize: "1.5rem", lineHeight: 1 }}>+</span>
          <span>{t("attachments.add")}</span>
        </button>
      )}

      {/* Centered popover overlay with grid of big icons */}
      {popoverOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.4)",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setPopoverOpen(false);
          }}
        >
          <div
            ref={popoverRef}
            style={{
              background: colors.bg,
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "12px",
              minWidth: "280px",
            }}
          >
            {[
              {
                ref: photoInputRef,
                icon: "\u{1F4F7}",
                label: t("attachments.photo"),
              },
              {
                ref: videoInputRef,
                icon: "\u{1F3AC}",
                label: t("attachments.video"),
              },
              {
                ref: fileInputRef,
                icon: "\u{1F4CE}",
                label: t("attachments.file"),
              },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  setPopoverOpen(false);
                  item.ref.current?.click();
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "16px 8px",
                  background: colors.bgAlt,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  color: colors.text,
                  fontSize: "0.85rem",
                }}
              >
                <span style={{ fontSize: "2.5rem", lineHeight: 1 }}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      {!readOnly && (
        <>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,application/pdf"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
        </>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null &&
        createPortal(
          <Lightbox
            attachments={viewable}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />,
          document.body,
        )}
    </div>
  );
}
